import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import type { KegiatanRapat } from '@/types/pocketbase'

async function fetchRapatList(kegiatanId: string) {
  const result = await pb.collection('kegiatan_rapat').getFullList({
    filter: `kegiatan = "${kegiatanId}"`,
    sort: 'tanggal',
    expand: 'notulis_id',
  })
  return result as KegiatanRapat[]
}

async function fetchRapat(id: string) {
  const record = await pb.collection('kegiatan_rapat').getOne(id, {
    expand: 'notulis_id,peserta_ids,kegiatan',
  })
  return record as KegiatanRapat
}

export function useRapatList(kegiatanId: string | undefined) {
  return useQuery({
    queryKey: ['kegiatan', kegiatanId, 'rapat'],
    queryFn: () => fetchRapatList(kegiatanId!),
    enabled: !!kegiatanId,
  })
}

export function useRapat(id: string | undefined) {
  return useQuery({
    queryKey: ['rapat', id],
    queryFn: () => fetchRapat(id!),
    enabled: !!id,
  })
}

export function useCreateRapat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<KegiatanRapat>) => {
      const record = await pb.collection('kegiatan_rapat').create(data)
      return record as KegiatanRapat
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', vars.kegiatan, 'rapat'] })
    },
  })
}

export function useUpdateRapat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<KegiatanRapat> }) => {
      const record = await pb.collection('kegiatan_rapat').update(id, data)
      return record as KegiatanRapat
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['rapat', vars.id] })
      // Also invalidate the list for the kegiatan
      const existing = qc.getQueryData<KegiatanRapat[]>(['kegiatan', vars.data.kegiatan, 'rapat'])
      if (existing) {
        qc.invalidateQueries({ queryKey: ['kegiatan', vars.data.kegiatan, 'rapat'] })
      }
    },
  })
}

export function useDeleteRapat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, kegiatanId }: { id: string; kegiatanId: string }) => {
      await pb.collection('kegiatan_rapat').delete(id)
      return kegiatanId
    },
    onSuccess: (kegiatanId) => {
      qc.invalidateQueries({ queryKey: ['kegiatan', kegiatanId, 'rapat'] })
    },
  })
}
